(() => {
  const selector = '.product__info-container .wht-neo-rail, .Vtl-InstagramFeedLayout__PostList';
  const states = new WeakMap();
  let controlId = 0;
  const maxScroll = (rail) => Math.max(0, rail.scrollWidth - rail.clientWidth);
  const clamp = (rail, value) => Math.max(0, Math.min(maxScroll(rail), value));

  const init = (rail) => {
    if (!rail.isConnected) return;
    const existing = states.get(rail);
    if (existing) {
      existing.loadMore();
      return;
    }
    if (rail.dataset.koraAppCarouselReady === 'true') return;
    rail.dataset.koraAppCarouselReady = 'true';
    const isReviews = rail.matches('.vtl-pr-masonry');
    const isStories = rail.matches('.Vtl-InstagramFeedLayout__PostList');
    const isVideos = !isReviews && !isStories;
    const isTopVideos = isVideos && Boolean(rail.closest('.product__info-container'));
    const itemSelector = isReviews
      ? '.vtl-pr-review-card'
      : isStories ? ':scope > .Vtl-InstagramFeedLayout__Post' : ':scope > .wht-neo-slide';
    rail.classList.add('kora-app-carousel', 'kora-app-carousel--mouse');
    rail.classList.add(isReviews ? 'kora-app-carousel--reviews' : isStories ? 'kora-app-carousel--stories' : 'kora-app-carousel--videos');
    if (isTopVideos) rail.classList.add('kora-app-carousel--video-stories');
    const listeners = new AbortController();
    const { signal } = listeners;
    let dragController;
    let wheelFrame = 0;
    let wheelTarget = null;
    let lastFrame = 0;
    let autoRevealTimer = 0;
    let autoRevealInterval = 0;
    let autoRevealStopped = true;
    let autoRevealStarted = false;
    let autoRevealVisible = false;
    let autoRevealObserver = null;
    let tryTopVideoReveal = () => {};

    const stopAutoReveal = () => {
      if (autoRevealStopped) return;
      autoRevealStopped = true;
      window.clearTimeout(autoRevealTimer);
      window.clearInterval(autoRevealInterval);
      autoRevealObserver?.disconnect();
      const current = rail.scrollLeft;
      rail.scrollTo({ left: current, behavior: 'auto' });
      rail.classList.remove('kora-app-carousel--auto-reveal');
    };

    const startTopVideoReveal = () => {
      const mobileRevealEnabled = window.matchMedia('(max-width: 749px)').matches ||
        new URLSearchParams(window.location.search).get('kora_mobile_test') === '1';
      if (!isTopVideos || !mobileRevealEnabled) return;
      if (document.documentElement.dataset.koraTopVideoAutoScrollStopped === 'true') return;
      autoRevealStopped = false;
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
        rail.addEventListener(type, () => {
          document.documentElement.dataset.koraTopVideoAutoScrollStopped = 'true';
          stopAutoReveal();
        }, { capture: true, passive: type !== 'keydown', signal });
      });
      const reveal = () => {
        if (autoRevealStarted || autoRevealStopped || !autoRevealVisible || !rail.isConnected || document.hidden) return;
        const slides = Array.from(rail.querySelectorAll(':scope > .wht-neo-slide'));
        const maximum = maxScroll(rail);
        if (slides.length < 2 || maximum <= 4 || rail.clientWidth <= 1) return;
        autoRevealStarted = true;
        autoRevealObserver?.disconnect();
        let previousTime = performance.now();
        let restartAt = 0;
        const speed = 30;
        rail.classList.add('kora-app-carousel--auto-reveal');
        const tick = () => {
          if (autoRevealStopped || !rail.isConnected) return stopAutoReveal();
          if (document.hidden) {
            previousTime = performance.now();
            return;
          }
          const time = performance.now();
          const currentMaximum = maxScroll(rail);
          if (currentMaximum <= 4) {
            previousTime = time;
            return;
          }
          if (restartAt) {
            if (time < restartAt) return;
            rail.scrollLeft = 0;
            restartAt = 0;
            previousTime = time;
          }
          const elapsed = Math.min(160, Math.max(0, time - previousTime));
          previousTime = time;
          rail.scrollLeft = Math.min(currentMaximum, rail.scrollLeft + Math.max(1, speed * elapsed / 1000));
          if (rail.scrollLeft >= currentMaximum - 1) restartAt = time + 900;
        };
        autoRevealInterval = window.setInterval(tick, 40);
      };
      tryTopVideoReveal = () => {
        if (autoRevealStarted || autoRevealStopped || !autoRevealVisible) return;
        if (autoRevealTimer) return;
        autoRevealTimer = window.setTimeout(() => {
          autoRevealTimer = 0;
          reveal();
        }, 220);
      };
      if ('IntersectionObserver' in window) {
        autoRevealObserver = new IntersectionObserver((entries) => {
          autoRevealVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.12);
          if (autoRevealVisible) tryTopVideoReveal();
        }, { threshold: [0.12, 0.3] });
        autoRevealObserver.observe(rail);
      } else {
        autoRevealVisible = true;
        tryTopVideoReveal();
      }
      document.addEventListener('visibilitychange', tryTopVideoReveal, { signal });
    };

    const stopWheel = () => {
      cancelAnimationFrame(wheelFrame);
      wheelFrame = 0;
      wheelTarget = null;
      rail.classList.remove('kora-app-carousel--direct');
    };

    const animateWheel = (time) => {
      if (!rail.isConnected || wheelTarget === null) return stopWheel();
      const elapsed = Math.min(64, Math.max(1, time - lastFrame));
      lastFrame = time;
      wheelTarget = clamp(rail, wheelTarget);
      const remaining = wheelTarget - rail.scrollLeft;
      if (Math.abs(remaining) < 1) {
        rail.scrollLeft = wheelTarget;
        stopWheel();
        return;
      }
      const movement = remaining * (1 - Math.exp(-elapsed / 55));
      rail.scrollLeft += Math.sign(movement) * Math.max(1, Math.abs(movement));
      wheelFrame = requestAnimationFrame(animateWheel);
    };

    rail.addEventListener('wheel', (event) => {
      stopAutoReveal();
      if (event.ctrlKey || event.metaKey || dragController?.dragging || maxScroll(rail) <= 1) return;
      const rawDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!rawDelta || !event.cancelable) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rail.clientWidth : 1;
      const distanceMultiplier = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? 1 : isReviews ? 1.35 : isStories ? 1 : isTopVideos ? 3.2 : 2.4;
      const delta = rawDelta * unit * distanceMultiplier;
      const pending = wheelTarget ?? rail.scrollLeft;
      if ((delta < 0 && pending <= 1) || (delta > 0 && pending >= maxScroll(rail) - 1)) return;
      event.preventDefault();
      rail.classList.add('kora-app-carousel--direct');
      wheelTarget = clamp(rail, pending + delta);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        rail.scrollLeft = wheelTarget;
        stopWheel();
      } else if (!wheelFrame) {
        lastFrame = performance.now();
        wheelFrame = requestAnimationFrame(animateWheel);
      }
    }, { passive: false, capture: true, signal });

    dragController = window.KoraDragScroll(rail, { onStart: () => {
      stopAutoReveal();
      stopWheel();
    } });

    controlId += 1;
    const control = document.createElement('div');
    control.className = `kora-app-scroll-control ${isReviews ? 'kora-app-scroll-control--reviews' : isStories ? 'kora-app-scroll-control--stories' : 'kora-app-scroll-control--videos'}`;
    const range = document.createElement('input');
    range.type = 'range';
    range.className = 'kora-app-scrollbar';
    range.min = '0'; range.max = '1000'; range.step = '1'; range.value = '0';
    range.id = `KoraAppScrollbar-${controlId}`;
    range.setAttribute('aria-label', isReviews ? 'Scroll customer reviews' : isStories ? 'Scroll Instagram stories' : 'Scroll product videos');
    if (!rail.id) rail.id = `KoraAppCarousel-${controlId}`;
    range.setAttribute('aria-controls', rail.id);
    control.append(range);
    if (isVideos && !isTopVideos) {
      const hint = document.createElement('p');
      hint.className = 'kora-app-scroll-hint';
      hint.textContent = 'Drag to explore videos';
      control.append(hint);
    }
    const host = isReviews
      ? rail.closest('.vtl-pr-main-widget__cards')
      : isStories ? rail.closest('.Vtl-InstagramFeedLayout__Content') : rail.closest('.wht-neo-rail-wrap');
    if (host) host.insertAdjacentElement('afterend', control);
    if (isStories) {
      const widget = rail.closest('.vtl-if-main-widget');
      let header = widget?.querySelector('.vtl-if-main-widget__header');
      if (widget && !header) {
        header = document.createElement('div');
        header.className = 'vtl-if-main-widget__header';
        const title = document.createElement('h2');
        title.textContent = 'Check out our Instagram';
        header.append(title);
        widget.prepend(header);
      }
      if (header && !header.querySelector('.kora-instagram-caption')) {
        const caption = document.createElement('p');
        caption.className = 'kora-instagram-caption';
        const profile = document.createElement('a');
        profile.href = 'https://www.instagram.com/koratextiles.pk/';
        profile.target = '_blank';
        profile.rel = 'noopener noreferrer';
        profile.textContent = '@koratextiles.pk';
        caption.append('Latest posts from ', profile, ' · Swipe or drag to explore.');
        header.append(caption);
      }
      const storySection = rail.closest('#vitals-end-section');
      const footerStart = document.querySelector('.shopify-section-group-footer-group');
      if (storySection && footerStart && storySection.nextElementSibling !== footerStart) {
        footerStart.insertAdjacentElement('beforebegin', storySection);
      }
    }

    let frame = 0;
    let loadingMore = false;
    let loadRetryTimer = 0;
    let loadRetryAttempts = 0;
    let lastItemCount = rail.querySelectorAll(itemSelector).length;
    const preserveVideoOrder = () => {
      if (isReviews || isStories) return;
      const slides = Array.from(rail.querySelectorAll(':scope > .wht-neo-slide'));
      const indexed = slides.map((slide) => ({ slide, index: Number(slide.dataset.ri) }));
      if (!indexed.length || !indexed.every(({ index }) => Number.isFinite(index))) return;
      const ordered = indexed.slice().sort((a, b) => a.index - b.index).map(({ slide }) => slide);
      if (ordered.some((slide, index) => slide !== slides[index])) rail.append(...ordered);
    };
    const getLoadMoreButton = () => isReviews
      ? rail.closest('.vtl-pr-main-widget')?.querySelector('.vtl-pr-main-widget__show-more-button')
      : isStories
        ? null
        : rail.closest('.wht-neo-widget')?.querySelector('.wht-neo-viewmore');
    const loadMore = () => {
      const button = getLoadMoreButton();
      if (loadingMore || !button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      loadingMore = true;
      lastItemCount = rail.querySelectorAll(itemSelector).length;
      button.click();
      clearTimeout(loadRetryTimer);
      loadRetryAttempts += 1;
      if (loadRetryAttempts < 4) {
        loadRetryTimer = window.setTimeout(() => {
          loadingMore = false;
          loadMore();
        }, 1800);
      }
    };
    const update = () => {
      frame = 0;
      const maximum = maxScroll(rail);
      const overflow = maximum > 4;
      control.hidden = isTopVideos || isStories || !overflow;
      range.disabled = !overflow;
      range.value = overflow ? String(Math.round(rail.scrollLeft / maximum * 1000)) : '0';
      if (!overflow) return;
      const width = range.clientWidth || rail.clientWidth;
      range.style.setProperty('--kora-app-scroll-thumb-width', `${Math.min(width, Math.max(36, width * rail.clientWidth / rail.scrollWidth))}px`);
      if (rail.scrollLeft >= maximum - rail.clientWidth * 0.75) loadMore();
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    rail.addEventListener('scroll', schedule, { passive: true, signal });
    range.addEventListener('pointerdown', () => { dragController.cancel(); stopWheel(); rail.classList.add('kora-app-carousel--direct'); }, { signal });
    range.addEventListener('input', () => {
      dragController.cancel();
      stopWheel();
      rail.classList.add('kora-app-carousel--direct');
      rail.scrollLeft = Number(range.value) / 1000 * maxScroll(rail);
      schedule();
    }, { signal });
    ['change', 'pointerup', 'pointercancel', 'blur'].forEach((type) => range.addEventListener(type, () => rail.classList.remove('kora-app-carousel--direct'), { signal }));

    const resize = new ResizeObserver(() => {
      schedule();
      tryTopVideoReveal();
    });
    resize.observe(rail);
    Array.from(rail.children).forEach((child) => resize.observe(child));
    const additions = new MutationObserver(() => {
      const currentItemCount = rail.querySelectorAll(itemSelector).length;
      if (currentItemCount > lastItemCount) {
        clearTimeout(loadRetryTimer);
        lastItemCount = currentItemCount;
        loadingMore = false;
        loadRetryAttempts = 0;
        preserveVideoOrder();
        if (!isTopVideos) requestAnimationFrame(loadMore);
      }
      rail.querySelectorAll(itemSelector).forEach((item) => resize.observe(item));
      schedule();
      tryTopVideoReveal();
    });
    additions.observe(rail, { childList: true, subtree: true });
    preserveVideoOrder();
    states.set(rail, { listeners, resize, additions, control, loadMore });
    if (!isTopVideos) requestAnimationFrame(loadMore);
    startTopVideoReveal();
    update();
  };

  const scan = (scope = document) => {
    if (scope instanceof Element && scope.matches(selector)) init(scope);
    scope.querySelectorAll?.(selector).forEach(init);
  };

  const start = () => {
    scan();
    let queued = false;
    new MutationObserver((mutations) => {
      if (queued || !mutations.some((mutation) => mutation.addedNodes.length)) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; scan(); });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
